/**
 * Leaving the administration application (SECURITY_AUDIT.md SEC-005).
 *
 * The same two facts the product's sign-out makes false at once — the
 * server's session and everything this browser cached because of it — with
 * one difference: an administrator's session reaches every account, so a
 * sign-out that did not happen has to be *said*, not quietly reloaded past.
 *
 * 1. Ask the API to end the session: a POST with a JSON body, which is what
 *    Better Auth's `/sign-out` accepts. This origin is in Better Auth's trusted
 *    origins; before it was, the request was refused with 403 and the reload
 *    that followed showed a session that was still alive.
 * 2. Check the answer. Only a 2xx means the session row is gone.
 * 3. Clear Apollo whatever the answer was — `clearStore`, which refetches
 *    nothing — so no account's data stays in memory either way.
 * 4. Reload. A full document load drops the router's state and any response
 *    still in flight. After a sign-out that worked, the shell finds no session
 *    and shows its signed-out screen. After one that did not, the page reloads
 *    with `?signOut=failed`, and the shell tells the administrator their
 *    session may still be active.
 */
import type { ApolloClient } from '@apollo/client'

import { SIGN_OUT_ENDPOINT } from './apollo.js'

/** The query parameter a failed sign-out reloads with. */
export const SIGN_OUT_FAILED_PARAM = 'signOut'
const FAILED = 'failed'

export interface SignOutDeps {
  /** Injected in tests. Defaults to replacing the document. */
  navigate?: (href: string) => void
  /** Injected in tests. Defaults to the runtime's own fetch. */
  fetch?: typeof globalThis.fetch
  /** Injected in tests. Defaults to the page's own address. */
  currentHref?: string
}

/** Ends the session. Resolves to whether the server confirmed it. */
export async function signOut(
  client: Pick<ApolloClient, 'clearStore'>,
  deps: SignOutDeps = {},
): Promise<boolean> {
  const request = deps.fetch ?? globalThis.fetch
  const go = deps.navigate ?? ((href: string) => window.location.replace(href))

  let ended = false
  try {
    const response = await request(SIGN_OUT_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    })
    ended = response.ok
  } catch {
    // Offline, or the API is down. The session may be alive; said below.
  }

  try {
    await client.clearStore()
  } catch {
    // A query in flight can reject the clear. The reload below drops the
    // whole cache regardless, so this must not stop it.
  }

  const next = new URL(deps.currentHref ?? window.location.href)
  if (ended) next.searchParams.delete(SIGN_OUT_FAILED_PARAM)
  else next.searchParams.set(SIGN_OUT_FAILED_PARAM, FAILED)
  go(next.toString())
  return ended
}

/** Whether this page is the reload after a sign-out the server did not confirm. */
export function signOutFailed(search: string): boolean {
  return new URLSearchParams(search).get(SIGN_OUT_FAILED_PARAM) === FAILED
}
